import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { flatApi, S3_BASE_URL } from './api';

// ─── Icons ───
const IconArrowLeft = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>);
const IconPlus = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const IconTrash = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>);
const IconEdit = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const IconMapPin = ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>);
const IconEye = ({ size = 14 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>);

const STATUS_COLORS = {
    ACTIVE: { bg: '#D1FAE5', text: '#059669' },
    RENTED: { bg: '#E4EFC0', text: '#2563EB' },
    EXPIRED: { bg: '#FEF3C7', text: '#D97706' },
    BLOCKED: { bg: '#FEE2E2', text: '#DC2626' },
    DELETED: { bg: '#E5E9E3', text: '#6B7280' },
};

const MyListingsPage = ({ onBack, onEditListing }) => {
    const { tokens, refreshAccessToken } = useAuth();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

    React.useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const fetchListings = async () => {
        setLoading(true);
        try {
            const res = await flatApi.getListings(tokens.access);
            setListings(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error('Failed to load listings', err);
            setListings([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchListings(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this listing?')) return;
        try {
            await flatApi.deleteListing(id, tokens.access);
            setListings(prev => prev.filter(l => l.id !== id));
            await refreshAccessToken();
        } catch (err) {
            alert('Failed to delete listing');
        }
    };

    const formatRent = (amt) => amt ? `₹${amt.toLocaleString('en-IN')}` : '₹0';

    const getTimeAgo = (dateStr) => {
        if (!dateStr) return 'Recently';
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        return `${days}d ago`;
    };

    const TYPE_GRADIENTS = {
        FLAT: 'linear-gradient(135deg, #3B82F6, #2563EB)',
        ROOM: 'linear-gradient(135deg, #10B981, #047857)',
        PG: 'linear-gradient(135deg, #F59E0B, #D97706)',
    };

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)', padding: '24px', color: 'white' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                        <IconArrowLeft />
                    </button>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', flex: 1, color: 'white' }}>My Listings</h2>
                    <button onClick={() => onEditListing(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '16px', padding: '10px 20px', color: 'white', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IconPlus size={16} /> New Listing
                    </button>
                </div>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: '#9CA3AF' }}>
                        <div style={{ fontSize: '18px', fontWeight: '600' }}>Loading listings...</div>
                    </div>
                ) : listings.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 40px', background: 'white', borderRadius: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#EFF6FF', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3B82F6', fontSize: '32px' }}>🏠</div>
                        <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', margin: '0 0 8px' }}>No listings yet</h3>
                        <p style={{ color: '#6B7280', margin: '0 0 24px', fontSize: '15px' }}>Create your first property listing to start receiving inquiries</p>
                        <button onClick={() => onEditListing(null)} style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', border: 'none', borderRadius: '16px', padding: '14px 32px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', boxShadow: '0 8px 24px rgba(47, 125, 87, 0.3)' }}>
                            Post Your First Listing
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {listings.map((item, index) => (
                            <div key={item.id || index} style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0' }}>
                                    {/* Image */}
                                    <div style={{ width: isMobile ? '100%' : '180px', minHeight: isMobile ? '160px' : '180px', height: isMobile ? '160px' : 'auto', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
                                        {item.images && item.images.length > 0 ? (
                                            <img src={`${S3_BASE_URL}${item.images[0]}`} alt="Listing" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <div style={{ width: '100%', height: '100%', background: '#DDE3DC', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '48px' }}>🏠</div>
                                        )}
                                        {/* Type badge */}
                                        <div style={{ position: 'absolute', top: '12px', left: '12px', background: TYPE_GRADIENTS[item.postType] || TYPE_GRADIENTS.FLAT, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '800' }}>
                                            {item.postType || 'FLAT'}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <div>
                                                <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>{formatRent(item.rent)}<span style={{ fontSize: '14px', fontWeight: '500', color: '#6B7280' }}>/mo</span></div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6B7280', fontSize: '14px', marginTop: '4px' }}>
                                                    <IconMapPin size={14} color="#10B981" />
                                                    <span>{item.location?.name || 'Location not set'}</span>
                                                </div>
                                            </div>
                                            <div style={{ background: STATUS_COLORS[item.status]?.bg || '#E5E9E3', color: STATUS_COLORS[item.status]?.text || '#6B7280', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '800' }}>
                                                {item.status || 'ACTIVE'}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {item.furnishing && (<span style={{ background: '#E5E9E3', color: '#4B5563', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>{item.furnishing === 'SEMI' ? 'Semi-Furnished' : item.furnishing === 'FURNISHED' ? 'Furnished' : 'Unfurnished'}</span>)}
                                            {item.gender && (<span style={{ background: '#E5E9E3', color: '#4B5563', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>{item.gender === 'ANY' ? 'Any Gender' : item.gender}</span>)}
                                            {item.negotiable && (<span style={{ background: '#D1FAE5', color: '#047857', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '700' }}>Negotiable</span>)}
                                        </div>

                                        <div style={{ display: 'flex', gap: '12px', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid #E5E9E3' }}>
                                            <button onClick={() => onEditListing(item)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: '12px', padding: '8px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', transition: '0.2s' }}>
                                                <IconEdit size={14} /> Edit
                                            </button>
                                            <button onClick={() => handleDelete(item.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FEF2F2', color: '#DC2626', border: 'none', borderRadius: '12px', padding: '8px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', transition: '0.2s' }}>
                                                <IconTrash size={14} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyListingsPage;
